import { useCallback, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { arrayOf, func, shape, string, bool } from 'prop-types';

import EntityToggle from './EntityToggle';
import { Wrapper, Fieldset, Legend, Inputs } from './style';

/**
 * EntityToggleList renders a fieldset of radio input's, which display entity
 * artwork in their label. When clicked, a function passed from the parent is
 * executed asynchronously, after the transform animation finishes.
 *
 * @method      EntityToggleList
 * @param       {PromiseState}   props.entities The result of an API call
 * @param       {Array}          props.entityIds The ID's of the entities to render
 * @param       {String}         props.entityType The type of entity to render
 * @param       {Array}          props.colors Array of colors for borders
 * @param       {String}         props.selectedEntity The currently selected entity in the parent
 * @param       {Function}       props.selectEntity Called to select an entity.
 * @constructor
 */
function EntityToggleList(props) {
  const {
    pending,
    entities,
    entityIds,
    entityType,
    colors,
    selectedEntity,
    selectEntity,
  } = props;

  /**
   * This component holds a local copy of the parent state in order to animate
   * before calling the change handler.
   */
  const [localSelectedEntity, setLocalSelectedEntity] =
    useState(selectedEntity);

  /**
   * Update the local state when the parent changes
   */
  useEffect(() => {
    if (selectedEntity !== localSelectedEntity) {
      setLocalSelectedEntity(selectedEntity);
    }
  }, [localSelectedEntity, selectedEntity]);

  // /** Don't render when we're still loading entity data */
  // if (pending && !value) return null;

  /**
   * Handle toggling an entity
   * @method handleEntityToggle
   * @param  {Event}     evt React SyntheticEvent
   */
  const handleEntityToggle = useCallback(
    function handleEntityToggle(event) {
      /** Get the entity id from the event target */
      const nextEntity = event.target.value;

      /** Set the local state to update the DOM and trigger the animation */
      setLocalSelectedEntity(nextEntity);

      /** After 80ms, update the parent state */
      setTimeout(() => selectEntity(nextEntity), 80);
    },
    [selectEntity]
  );

  return (
    <Wrapper>
      <Fieldset fade={pending}>
        <Legend>Select the {entityType} to display in the map</Legend>
        <Inputs>
          {entityIds.map((id, idx) => {
            const entity = entities.find((el) => el && el.id === id);

            return (
              <EntityToggle
                {...entity}
                key={`entity-toggle-${id}`}
                id={id}
                entityType={entityType}
                name={`entity-toggle-${entityIds.join(',')}`}
                isSelected={id === localSelectedEntity}
                color={colors[idx]}
                handleChange={handleEntityToggle}
              />
            );
          })}
        </Inputs>
      </Fieldset>
    </Wrapper>
  );
}

const entityPropType = shape();

EntityToggleList.propTypes = {
  colors: arrayOf(string).isRequired,
  entities: arrayOf(entityPropType).isRequired,
  entityIds: arrayOf(string).isRequired,
  entityType: string.isRequired,
  pending: bool.isRequired,
  selectedEntity: string.isRequired,
  selectEntity: func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const { entityIds, entityType } = ownProps;

  const entities = entityIds.map((id) => state.entity.byId[entityType][id]);

  return {
    entities: entities.map((el) => el.entity),
    pending: entities.reduce((acc, el) => el.loading || acc, false),
  };
}

export default connect(mapStateToProps)(EntityToggleList);
